import { ReactNode, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { ExampleQuestions } from './example-questions';
import { DraftType } from '../../../../../../../../../../../backend/src/types/types';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { Case, Subset } from '@/api/types/backend-dto-types';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/new/components/shadcn/dialog';
import { Input } from '@/new/components/shadcn/input';
import { ExecuteKimSearch, KimMode } from '@/types';

interface CustomQuestionInputProps {
  placeholder: string;
  title: string;
  onSubmit: (question: string) => void;
}

const CustomQuestionInput = ({ placeholder, title, onSubmit }: CustomQuestionInputProps) => {
  const [question, setQuestion] = useState('');

  const handleSubmit = () => {
    if (question.trim()) {
      onSubmit(question);
      trackCustomEvent(TrackingEventsV2.ThreadOnboardingCustomQuestion);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
      <h4 className="mb-2 text-sm font-medium text-gray-900">{title}</h4>
      <div className="flex gap-2">
        <Input
          placeholder={placeholder}
          className="w-full bg-white"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleSubmit();
            }
          }}
        />
        <Button variant="primary" onClick={handleSubmit}>
          Submit
        </Button>
      </div>
    </div>
  );
};

type KimFlowType = 'question' | 'extraction' | 'draft';

interface KimOnboardingModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  flowType: KimFlowType;
  subset: Subset | undefined;
  executeSearch: ExecuteKimSearch;
  setMode: (mode: KimMode) => void;
  setDraftType: (draftType: DraftType | null) => void;
  setQuestionValue: (value: string) => void;
}

interface FlowConfig {
  testId: string;
  title: string;
  mode: KimMode;
  exampleQuestionsKey: 'question' | 'extract' | 'draft';
  introduction: ReactNode;
  customInputPlaceholder: string;
  customInputTitle: string;
  onSubmit: (question: string, setIsOpen: (isOpen: boolean) => void) => void;
}

export const KimOnboardingModal = ({
  isOpen,
  setIsOpen,
  flowType,
  subset,
  executeSearch,
  setMode,
  setDraftType,
  setQuestionValue,
}: KimOnboardingModalProps) => {
  const { t } = useTranslation();

  const flowConfigs: Record<KimFlowType, FlowConfig> = {
    question: {
      testId: 'kim-question-onboarding-modal',
      title: 'Question Flow',
      mode: KimMode.QuestionFlow,
      exampleQuestionsKey: 'question',
      introduction: (
        <div className="flex flex-col gap-4 rounded-lg border-2 border-purple-200 bg-purple-50 p-6 text-left shadow-sm">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-purple-900">Using the Question Flow</h3>
            <div className="flex flex-col gap-3 text-sm text-gray-700">
              <p>
                The question flow is used for asking questions over all of the information in your case, including
                documents, facts and inconsistencies.
              </p>
              <p>
                Your question can be broad e.g. "What are the main issues in this case?", or more specific "When was{' '}
                {t('witness')} x born?".
              </p>
              <p>
                The result is an answer in prose, with references to the facts and document text used in the answer.
              </p>
            </div>
          </div>
        </div>
      ),
      customInputPlaceholder: 'Enter your question here...',
      customInputTitle: 'Or ask your own question',
      onSubmit: (question, setIsOpen) => {
        setMode(KimMode.QuestionFlow);
        setQuestionValue(question);
        setIsOpen(false);
        executeSearch({ question, questionMode: KimMode.QuestionFlow });
      },
    },
    extraction: {
      testId: 'kim-extraction-onboarding-modal',
      title: 'Extraction Flow',
      mode: KimMode.ExtractionFlow,
      exampleQuestionsKey: 'extract',
      introduction: (
        <div className="flex flex-col gap-4 rounded-lg border-2 border-purple-200 bg-purple-50 p-6 text-left shadow-sm">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-purple-900">Using the Extraction Flow</h3>
            <div className="flex flex-col gap-3 text-sm text-gray-700">
              <p>
                The extraction flow is used for tabular review of documents. It can extract information from documents
                and present it in tables. This can be simple keyword extraction, or semantic extraction (i.e. 'extract
                criticisms of X), or classification at a document level. It is exhaustive and should be used for
                identifying references to topics or entities across the whole corpus of documents. It will pull on the
                facts (referenced in blue) as well as extracts from the rest of the document.
              </p>
              <p>
                For example, you could use the extraction flow to identify all mentions of a particular {t('witness')}{' '}
                in a case, to make a table of every {t('witness')} and their relationship to the case, or to summarise
                each document and explain its connection to X.
              </p>

              <p className="mt-2 rounded-md bg-white p-3 text-xs font-medium text-gray-600 shadow-sm">
                When you submit an extraction flow, a 'plan' will be returned, this plan will detail what the workflow
                is going to do, and will allow you to change some fields, such as column names.
              </p>
            </div>
          </div>
        </div>
      ),
      customInputPlaceholder: 'Enter extraction question...',
      customInputTitle: 'Or create your own extraction',
      onSubmit: (question, setIsOpen) => {
        setMode(KimMode.ExtractionFlow);
        setQuestionValue(question);
        setIsOpen(false);
        executeSearch({ question, questionMode: KimMode.ExtractionFlow });
      },
    },
    draft: {
      testId: 'kim-draft-onboarding-modal',
      title: 'Draft Flow',
      mode: KimMode.DraftFlow,
      exampleQuestionsKey: 'draft',
      introduction: (
        <div className="flex flex-col gap-4 rounded-lg border-2 border-purple-200 bg-purple-50 p-6 text-left shadow-sm">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-purple-900">Using the Draft Flow</h3>

            <div className="flex flex-col gap-3 text-sm text-gray-700">
              <p>
                Kim can help you draft documents based on the facts and evidence from your case, such as memos, briefs,
                correspondence, articles, advice notes and more. Wexler is designed to be broad, and can be thought of
                as a few pages of analysis which you can then tweak into the necessary template.
              </p>
              <p>
                There are two pre-set options, {t('letter of claim')} and letter of rebuttal, to help draft the
                beginning of fact-based correspondence.
              </p>
            </div>
            <div className="rounded-md bg-white p-3 text-xs font-medium text-gray-600 shadow-sm">
              <p>
                When you submit a query, a 'plan' will be returned, this plan will detail what the workflow is going to
                do, and will allow you to change some fields, such as the tone.
              </p>
            </div>
          </div>
        </div>
      ),
      customInputPlaceholder: 'Enter draft instructions...',
      customInputTitle: 'Or create your own draft',
      onSubmit: (question, setIsOpen) => {
        setMode(KimMode.DraftFlow);
        setDraftType(DraftType.Claim);
        setQuestionValue(question);
        setIsOpen(false);
        executeSearch({ question, questionMode: KimMode.DraftFlow });
      },
    },
  };

  const config = flowConfigs[flowType];
  const exampleQuestions = subset?.exampleKimQuestions?.[config.exampleQuestionsKey] ?? [];

  const handleQuestionSubmit = (question: string) => {
    config.onSubmit(question, setIsOpen);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="max-w-6xl" data-testid={config.testId}>
        <DialogHeader>
          <DialogTitle>{config.title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {config.introduction}

          <div className="flex flex-col gap-2 py-4">
            <div className="text-left">
              <p className="text-sm font-medium text-gray-900 underline">
                Here are some potential questions for your case that you could use in this flow:
              </p>
            </div>

            <ExampleQuestions questions={exampleQuestions} onQuestionClick={handleQuestionSubmit} />

            <div className="mt-4">
              <CustomQuestionInput
                placeholder={config.customInputPlaceholder}
                title={config.customInputTitle}
                onSubmit={handleQuestionSubmit}
              />
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
